// Pixel-art de la oficina (vista cenital): los monigotes y su silla, en rectángulos de píxel entero sobre un canvas de
// «píxeles de arte» que después se amplía por un factor ENTERO (nítido de verdad, nada de PNG reescalado).
// Los colores de los muebles y del papel salen de la paleta única de la oficina (features/oficina/ui/paleta.js); el arte
// del personaje (piel, pelo, ojos, gafas) es suyo. El mobiliario de la planta se dibuja en features/oficina/mundo/dibujar.ts.
import { escena, oficina } from '../../features/oficina/ui/paleta.js';

/** Gesto del personaje: lo que está haciendo. */
export type Animacion = 'teclea' | 'lee' | 'piensa' | 'duerme';

/* ------------------------------------------------------------------ *
 * Retratos — una cara distinta y estable por semilla (el agente)
 * ------------------------------------------------------------------ */

export interface RetratoPixel {
  piel: string;
  pelo: string;
  camisa: string;
  /** 0 corto · 1 recogido · 2 rizado · 3 raya · 4 calvo · 5 melena · 6 flequillo */
  peinado: number;
  gafas: boolean;
  barba: boolean;
  /** 0 estrecho · 1 medio · 2 ancho — tres siluetas de cuerpo distintas */
  cuerpo: number;
}

const PIEL = ['#E8B98E', '#D9A06B', '#C68F5F', '#A9714A', '#F0C9A4', '#8D5A3B'];
const PELO = ['#3A2F28', '#6E4632', '#1D1815', '#8A6A45', '#4E3B30', '#B08A55'];
// 04-11 (visual.md §2.A.10): sin camisas que se confundan con los estados (#57538A ≈ violeta «no puede seguir», #8E5B76).
const CAMISA = ['#3E5C8A', '#7A6A58', '#6F7A45', '#41786D', '#A2694A', '#5E6B7A', '#9C4F55', '#4C6E96'];

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function retratoDe(semilla: string): RetratoPixel {
  const h = hash(semilla);
  return {
    piel: PIEL[h % PIEL.length]!,
    pelo: PELO[(h >>> 5) % PELO.length]!,
    camisa: CAMISA[(h >>> 10) % CAMISA.length]!,
    peinado: (h >>> 15) % 7,
    gafas: ((h >>> 20) & 3) === 0,
    barba: ((h >>> 22) & 3) === 0,
    cuerpo: (h >>> 25) % 3,
  };
}

export type Ctx = CanvasRenderingContext2D;

const TINTA = '#0D0B08'; // pupilas y ojos cerrados

const r = (ctx: Ctx, x: number, y: number, w: number, h: number, c: string) => {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

/** Silla de oficina vista desde delante: el respaldo asoma a los lados de la persona (gris de la escena, sin brillos). */
export function silla(ctx: Ctx, cx: number, y: number): void {
  const w = 30;
  const x = cx - Math.round(w / 2);
  r(ctx, x + 2, y + 18, w - 4, 3, escena.sombra);
  r(ctx, x, y - 8, w, 26, oficina.control); // respaldo alto: asoma por detrás de la persona
  r(ctx, x + 1, y - 7, w - 2, 2, oficina.pulsado);
}

/* ------------------------------------------------------------------ *
 * Personajes
 * ------------------------------------------------------------------ */

/**
 * Personaje sentado, 4 frames. El gesto dice el dato:
 *   teclea → las manos bajan y suben sobre el teclado
 *   lee    → sostiene una hoja y la cabeza se ladea
 *   piensa → burbuja de puntos y cabeza que asiente
 *   duerme → cabeza caída y hombros que respiran (la Z va en el DOM)
 */
export function persona(
  ctx: Ctx,
  cx: number,
  y: number,
  ret: RetratoPixel,
  anim: Animacion,
  frame: number,
  dePie = false,
  /** 29-10 — 1 = normal; menos, medio transparente (los que se van). Quien llama deja el alfa como estaba. */
  alfa = 1,
): void {
  const f = frame & 3;
  const dorm = anim === 'duerme';
  const respira = dorm ? (f === 1 || f === 2 ? 1 : 0) : 0;
  const pielOsc = lighten(ret.piel, -34);
  const camisaOsc = lighten(ret.camisa, -22);
  const camisaLuz = lighten(ret.camisa, 20);
  const peloLuz = lighten(ret.pelo, 26);

  // sombra suave a los pies (dos óvalos: núcleo + difuminado)
  ctx.globalAlpha = 0.28 * alfa;
  r(ctx, cx - 10, y + (dePie ? 30 : 23), 20, 4, '#000000');
  ctx.globalAlpha = 0.14 * alfa;
  r(ctx, cx - 13, y + (dePie ? 29 : 22), 26, 6, '#000000');
  ctx.globalAlpha = alfa;

  if (dePie) {
    // piernas
    r(ctx, cx - 6, y + 18, 5, 13, '#2E3440');
    r(ctx, cx + 1, y + 18, 5, 13, '#2E3440');
    r(ctx, cx - 7, y + 29, 6, 3, '#1B1F26');
    r(ctx, cx + 1, y + 29, 6, 3, '#1B1F26');
  }

  // torso — tres complexiones (hombro estrecho / medio / ancho)
  const cuerpoY = y + 11 + (dorm ? 3 : 0) + respira;
  const cuerpoH = (dePie ? 10 : 13) + (ret.cuerpo === 2 ? 1 : 0);
  const medio = 7 + ret.cuerpo * 2; // 7 · 9 · 11 a cada lado
  r(ctx, cx - medio, cuerpoY, medio * 2, cuerpoH, ret.camisa);
  r(ctx, cx - medio, cuerpoY, medio * 2, 1, camisaLuz);
  r(ctx, cx - medio, cuerpoY, 1, cuerpoH, camisaOsc);
  r(ctx, cx + medio - 1, cuerpoY, 1, cuerpoH, camisaOsc);
  if (ret.cuerpo === 2) r(ctx, cx - medio + 2, cuerpoY + 3, medio * 2 - 4, 1, camisaOsc);
  r(ctx, cx - 3, cuerpoY, 6, 3, camisaOsc); // cuello de la camisa
  r(ctx, cx - 1, cuerpoY, 2, 4, lighten(ret.camisa, -34));

  // brazos y manos (lo que se mueve al teclear)
  const dy = anim === 'teclea' ? (f === 0 || f === 2 ? 0 : f === 1 ? 1 : 2) : 0;
  r(ctx, cx - medio - 3, cuerpoY + 3, 3, 8, ret.camisa);
  r(ctx, cx + medio, cuerpoY + 3, 3, 8, ret.camisa);
  r(ctx, cx - medio - 3, cuerpoY + 10 + dy, 3, 3, ret.piel);
  r(ctx, cx + medio, cuerpoY + 10 + (anim === 'teclea' ? 2 - dy : 0), 3, 3, ret.piel);

  // hoja cuando lee
  if (anim === 'lee') {
    const inc = f === 1 || f === 2 ? 1 : 0;
    r(ctx, cx - 7, cuerpoY + 4 + inc, 14, 10, escena.papel);
    r(ctx, cx - 7, cuerpoY + 4 + inc, 14, 1, oficina.texto);
    r(ctx, cx - 5, cuerpoY + 6 + inc, 10, 1, oficina.texto3);
    r(ctx, cx - 5, cuerpoY + 8 + inc, 7, 1, oficina.texto3);
    r(ctx, cx - 5, cuerpoY + 10 + inc, 9, 1, oficina.texto3);
  }

  // ---- cabeza ----
  const cabY = y + (dorm ? 2 : 0) + respira - (dePie ? 1 : 0);
  const hx = cx - 7;
  const hw = 14;
  r(ctx, hx, cabY, hw, 12, ret.piel);          // cara
  r(ctx, hx, cabY, hw, 1, lighten(ret.piel, 18)); // luz en la frente
  r(ctx, hx, cabY + 11, hw, 1, pielOsc);        // barbilla
  r(ctx, hx - 1, cabY + 4, 1, 4, ret.piel);     // orejas
  r(ctx, hx + hw, cabY + 4, 1, 4, ret.piel);

  // pelo, con volumen
  const pelo = ret.pelo;
  switch (ret.peinado) {
    case 1: // recogido con moño
      r(ctx, hx - 1, cabY - 3, hw + 2, 5, pelo);
      r(ctx, hx - 1, cabY + 2, 2, 4, pelo);
      r(ctx, hx + hw - 1, cabY + 2, 2, 4, pelo);
      r(ctx, hx + hw - 1, cabY - 6, 5, 5, pelo);
      r(ctx, hx + hw, cabY - 5, 3, 2, peloLuz);
      break;
    case 2: // rizado
      r(ctx, hx - 1, cabY - 4, hw + 2, 6, pelo);
      r(ctx, hx - 3, cabY - 2, 3, 6, pelo);
      r(ctx, hx + hw, cabY - 2, 3, 6, pelo);
      r(ctx, hx + 1, cabY - 6, 4, 3, pelo);
      r(ctx, hx + 8, cabY - 6, 4, 3, pelo);
      r(ctx, hx + 2, cabY - 3, 4, 1, peloLuz);
      break;
    case 3: // raya al lado
      r(ctx, hx - 1, cabY - 3, hw + 2, 5, pelo);
      r(ctx, hx + 1, cabY + 1, 5, 2, pelo);
      r(ctx, hx + 2, cabY - 2, 7, 1, peloLuz);
      break;
    case 4: // entradas
      r(ctx, hx + 1, cabY - 2, hw - 2, 3, pelo);
      r(ctx, hx, cabY - 1, 2, 3, pelo);
      r(ctx, hx + hw - 2, cabY - 1, 2, 3, pelo);
      break;
    case 5: // melena larga
      r(ctx, hx - 2, cabY - 4, hw + 4, 6, pelo);
      r(ctx, hx - 3, cabY - 1, 3, 14, pelo);
      r(ctx, hx + hw, cabY - 1, 3, 14, pelo);
      r(ctx, hx - 3, cabY + 12, 4, 2, pelo);
      r(ctx, hx + hw - 1, cabY + 12, 4, 2, pelo);
      r(ctx, hx, cabY - 3, 6, 1, peloLuz);
      break;
    case 6: // flequillo recto
      r(ctx, hx - 2, cabY - 4, hw + 4, 7, pelo);
      r(ctx, hx - 2, cabY + 1, 2, 8, pelo);
      r(ctx, hx + hw, cabY + 1, 2, 8, pelo);
      r(ctx, hx + 1, cabY - 2, 8, 1, peloLuz);
      break;
    default: // corto
      r(ctx, hx - 1, cabY - 3, hw + 2, 5, pelo);
      r(ctx, hx + 2, cabY - 2, 6, 1, peloLuz);
  }

  // cejas
  if (!dorm) {
    r(ctx, hx + 2, cabY + 4, 3, 1, pelo);
    r(ctx, hx + 9, cabY + 4, 3, 1, pelo);
  }

  // ojos (cerrados si duerme)
  if (dorm) {
    r(ctx, hx + 2, cabY + 7, 3, 1, TINTA);
    r(ctx, hx + 9, cabY + 7, 3, 1, TINTA);
  } else {
    r(ctx, hx + 2, cabY + 6, 3, 2, '#F6F1E6');
    r(ctx, hx + 9, cabY + 6, 3, 2, '#F6F1E6');
    const mira = anim === 'piensa' && (f === 1 || f === 2) ? 1 : 0;
    r(ctx, hx + 3 + mira, cabY + 6, 1, 2, TINTA);
    r(ctx, hx + 10 + mira, cabY + 6, 1, 2, TINTA);
  }

  if (ret.gafas) {
    r(ctx, hx + 1, cabY + 5, 5, 4, '#20242B');
    r(ctx, hx + 8, cabY + 5, 5, 4, '#20242B');
    r(ctx, hx + 2, cabY + 6, 3, 2, '#9FD6E8');
    r(ctx, hx + 9, cabY + 6, 3, 2, '#9FD6E8');
    r(ctx, hx + 6, cabY + 6, 2, 1, '#20242B');
  }

  // nariz y boca
  r(ctx, hx + 6, cabY + 8, 2, 1, pielOsc);
  if (dorm) {
    r(ctx, hx + 6, cabY + 10, 2, 1, pielOsc);
  } else {
    r(ctx, hx + 5, cabY + 10, 4, 1, '#8A4A44');
  }

  // barba
  if (ret.barba) {
    r(ctx, hx, cabY + 8, 2, 4, pelo);
    r(ctx, hx + hw - 2, cabY + 8, 2, 4, pelo);
    r(ctx, hx + 2, cabY + 11, hw - 4, 2, pelo);
    r(ctx, hx + 4, cabY + 9, 6, 1, lighten(pelo, -12));
  }
}

export function lighten(hex: string, amt: number): string {
  const v = parseInt(hex.slice(1), 16);
  const c = (x: number) => Math.max(0, Math.min(255, x + amt));
  return `#${[c(v >> 16), c((v >> 8) & 255), c(v & 255)].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}
